import { emptyDisplayValue } from 'constants/common';
import {
  CoderbyteAssessmentStageActionCandidateStatus,
  CognitiveTestAssessmentStageActionCandidateStatus,
  DeiConsentEventType,
  DeiStageActionCandidateFragment,
  DeiStageActionCandidateStatus,
  EnglishProficiencyAssessmentStageActionCandidateStatus,
  FormCandidateModelFragment,
  PersonalityTestAssessmentStageActionCandidateStatus,
  RssAssessmentCandidateFragment,
  RssAssessmentCandidateStatus,
  RssAssessmentType,
} from 'generated/graphql';
import { formatTitleCase } from 'helpers/strings';
import { Button, Tooltip } from '@spotted-zebra-uk/ui-components';
import {
  IFilteredTestActionCandidate,
  TestActionType,
} from '../UserResets.interface';
import styles from './ActionsResultTable.module.scss';

export const getTestActionName = (item: IFilteredTestActionCandidate) => {
  switch (item.type) {
    case TestActionType.COGNITIVE:
      return formatTitleCase(item.cognitiveKind ?? '');
    case TestActionType.PERSONALITY:
      return 'Personality';
    case TestActionType.CODERBYTE:
      return item.coderbyteAssessmentName
        ? `Coderbyte - ${item.coderbyteAssessmentName}`
        : 'Coderbyte';
    case TestActionType.ENGLISH_PROFICIENCY:
      return 'English proficiency';
  }
};

const isNotStarted = (item: IFilteredTestActionCandidate) =>
  item.status === CognitiveTestAssessmentStageActionCandidateStatus.NotStarted ||
  item.status === PersonalityTestAssessmentStageActionCandidateStatus.NotStarted ||
  item.status === CoderbyteAssessmentStageActionCandidateStatus.NotStarted ||
  item.status ===
    EnglishProficiencyAssessmentStageActionCandidateStatus.NotStarted;

const hasExtraTime = (item: IFilteredTestActionCandidate) =>
  item.type === TestActionType.COGNITIVE ||
  item.type === TestActionType.CODERBYTE;

// Coderbyte's extra time travels with the candidate's invite link, so it can
// only be changed before they start (the API refuses it afterwards).
const isExtraTimeLocked = (item: IFilteredTestActionCandidate) =>
  item.type === TestActionType.CODERBYTE && !isNotStarted(item);

const ACTION_STATUS = {
  COMPLETED: 'COMPLETED',
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
} as const;

export const getActionStatus = (
  rssSoftSkillAssessmentCandidateStatus?: RssAssessmentCandidateStatus,
  rssTechnicalSkillAssessmentCandidateStatus?: RssAssessmentCandidateStatus
) => {
  const isSubmitted = (status?: RssAssessmentCandidateStatus) =>
    status === RssAssessmentCandidateStatus.Submitted;

  const isNotStarted = (status?: RssAssessmentCandidateStatus) =>
    !status || status === RssAssessmentCandidateStatus.NotStarted;

  const getStatusForSingleAssessment = (
    status?: RssAssessmentCandidateStatus
  ) =>
    isSubmitted(status) ? ACTION_STATUS.COMPLETED : ACTION_STATUS.NOT_STARTED;

  if (!rssSoftSkillAssessmentCandidateStatus) {
    return getStatusForSingleAssessment(
      rssTechnicalSkillAssessmentCandidateStatus
    );
  }

  if (!rssTechnicalSkillAssessmentCandidateStatus) {
    return getStatusForSingleAssessment(rssSoftSkillAssessmentCandidateStatus);
  }

  const areBothStatuses = (
    predicate: (status?: RssAssessmentCandidateStatus) => boolean
  ) =>
    predicate(rssSoftSkillAssessmentCandidateStatus) &&
    predicate(rssTechnicalSkillAssessmentCandidateStatus);

  if (areBothStatuses(isSubmitted)) {
    return ACTION_STATUS.COMPLETED;
  }

  if (areBothStatuses(isNotStarted)) {
    return ACTION_STATUS.NOT_STARTED;
  }

  return ACTION_STATUS.IN_PROGRESS;
};

export const ModalContent = {
  resetTest: {
    message: (projectName: string, testType: string) =>
      `Please confirm you wish to delete the current results for this candidate's ${testType} assessment for "${projectName}". This cannot be undone.`,
    okButtonChildren: 'test',
  },
  resetStage: {
    message:
      'You are about to reset the stage and all the tests in the stage, including any soft skill calculations. This cannot be undone.',
    okButtonChildren: 'stage',
  },
};

const TEST_ACTION_TYPE_ORDER = [
  TestActionType.COGNITIVE,
  TestActionType.PERSONALITY,
  TestActionType.CODERBYTE,
  TestActionType.ENGLISH_PROFICIENCY,
];

// Cognitive tests first (by kind), then personality, Coderbyte and English
// proficiency.
export const getSortedTestResults = (
  filteredTestActionCandidates: IFilteredTestActionCandidate[]
) =>
  filteredTestActionCandidates?.slice().sort((a, b) => {
    const typeOrder =
      TEST_ACTION_TYPE_ORDER.indexOf(a.type) -
      TEST_ACTION_TYPE_ORDER.indexOf(b.type);
    if (typeOrder !== 0) return typeOrder;
    return (a.cognitiveKind ?? '') < (b.cognitiveKind ?? '') ? -1 : 1;
  });

const formatConsentEvents = (
  events: DeiStageActionCandidateFragment['consentEvents']
) =>
  events
    .map(
      ({ event, occurredAt, actorUserId }) =>
        `${formatTitleCase(event)} · ${new Date(
          occurredAt
        ).toLocaleString()} · ${
          actorUserId == null ? 'system' : `user ${actorUserId}`
        }`
    )
    .join('\n');

export const canRevokeDeiConsent = (
  deiForm: DeiStageActionCandidateFragment
) => {
  const latest = deiForm.consentEvents[deiForm.consentEvents.length - 1];
  return (
    latest?.event === DeiConsentEventType.Given ||
    deiForm.status === DeiStageActionCandidateStatus.Submitted
  );
};

export const convertToRows = (
  filteredTestActionCandidates: IFilteredTestActionCandidate[],
  rssAssessmentCandidates: RssAssessmentCandidateFragment[],
  handleMitigateCircumstance: (
    isDefault: boolean,
    item: IFilteredTestActionCandidate
  ) => void,
  handleResetTestOnClick: (item: IFilteredTestActionCandidate) => void,
  isRssAssessmentCandidateReOpenLoading: boolean,
  handleRssAssessmentCandidateReOpen: () => void,
  handleResetDeiModal: () => void,
  handleRevokeConsentModal: () => void,
  rsForm?: FormCandidateModelFragment,
  deiForm?: DeiStageActionCandidateFragment | null
) => {
  const rowsTmp = getSortedTestResults(filteredTestActionCandidates).map(
    (item: IFilteredTestActionCandidate, index: number) => {
      return {
        id: index.toString(),
        name: getTestActionName(item),
        actionStatus: formatTitleCase(item.status),
        assessmentId: item?.assessmentId || emptyDisplayValue,
        actionCandidateId: item?.id || emptyDisplayValue,
        extend: hasExtraTime(item) ? (
          <Tooltip
            content="Extra time can only be changed before the candidate starts. Reset the test first."
            disabled={!isExtraTimeLocked(item)}
            disableAriaLabel
          >
            <Button
              size="small"
              variant={
                !item.timeModifierPercentage ? 'secondary' : 'primary'
              }
              onClick={() => handleMitigateCircumstance(true, item)}
              disabled={isExtraTimeLocked(item)}
              className={
                !item.timeModifierPercentage
                  ? styles.defaultTimeButton
                  : styles.extraTimeButton
              }
            >
              Extra time
            </Button>
          </Tooltip>
        ) : undefined,

        update: (
          <div className={styles.updateButtonContainer}>
            <Button
              size="small"
              disabled={isNotStarted(item)}
              onClick={() => handleResetTestOnClick(item)}
            >
              Reset test
            </Button>
          </div>
        ),
      };
    }
  );
  if (Boolean(rssAssessmentCandidates.length)) {
    const rssSoftSkillAssessmentCandidate = rssAssessmentCandidates.find(
      rssAssessmentCandidate =>
        rssAssessmentCandidate.rssAssessment.type ===
        RssAssessmentType.SoftSkill
    );

    const rssTechnicalSkillAssessmentCandidate = rssAssessmentCandidates.find(
      rssAssessmentCandidate =>
        rssAssessmentCandidate.rssAssessment.type ===
        RssAssessmentType.TechnicalSkill
    );

    const status = getActionStatus(
      rssSoftSkillAssessmentCandidate?.status,
      rssTechnicalSkillAssessmentCandidate?.status
    );

    rowsTmp.push({
      id: (rowsTmp.length + 1).toString(),
      name: 'Skills self assessment',
      actionStatus: formatTitleCase(status),
      assessmentId: emptyDisplayValue,
      actionCandidateId: emptyDisplayValue,
      extend: undefined,
      update: (
        <div className={styles.updateButtonContainer}>
          <Button
            size="small"
            loading={isRssAssessmentCandidateReOpenLoading}
            onClick={handleRssAssessmentCandidateReOpen}
          >
            Reopen
          </Button>
        </div>
      ),
    });
  }

  if (rsForm) {
    rowsTmp.push({
      id: (rowsTmp.length + 1).toString(),
      name: 'RS form',
      actionStatus: formatTitleCase(rsForm?.status),
      assessmentId: emptyDisplayValue,
      actionCandidateId: emptyDisplayValue,
      extend: undefined,
      update: (
        <div className={styles.updateButtonContainer}>
          <Button size="small" onClick={handleResetDeiModal}>
            Reopen
          </Button>
        </div>
      ),
    });
  }
  if (deiForm) {
    rowsTmp.push({
      id: (rowsTmp.length + 1).toString(),
      name: 'Demographic information form',
      actionStatus: formatTitleCase(deiForm.status),
      assessmentId: emptyDisplayValue,
      actionCandidateId: emptyDisplayValue,
      extend: undefined,
      update: (
        <div className={styles.updateButtonContainer}>
          <Tooltip
            content={
              <span style={{ whiteSpace: 'pre-line' }}>
                {deiForm.consentEvents.length > 0
                  ? formatConsentEvents(deiForm.consentEvents)
                  : 'No consent history'}
              </span>
            }
          >
            <Button size="small" variant="secondary">
              Consent history
            </Button>
          </Tooltip>
          {canRevokeDeiConsent(deiForm) && (
            <Button
              size="small"
              variant="negativePrimary"
              onClick={handleRevokeConsentModal}
            >
              Revoke consent
            </Button>
          )}
          <Button size="small" onClick={handleResetDeiModal}>
            Reopen
          </Button>
        </div>
      ),
    });
  }
  return rowsTmp;
};
